import { useState } from 'react';
import type { RecurrenceRule } from '@kinapp/shared';
import { Modal } from './Modal';

interface RecurrenceSelectProps {
  value: RecurrenceRule | null;
  onChange: (rule: RecurrenceRule | null) => void;
  referenceDate?: string; // YYYY-MM-DD for smart label generation
}

const DAYS_SHORT = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const DAYS_RFC = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
const DAYS_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const ORDINALS = ['first', 'second', 'third', 'fourth', 'last'];
const ORDINAL_LABELS = ['1st', '2nd', '3rd', '4th', 'Last'];

function parseRefDate(iso?: string): Date | null {
  if (!iso) return null;
  const d = new Date(iso + 'T12:00:00');
  return isNaN(d.getTime()) ? null : d;
}

function ordinalForDate(date: Date): { position: 1 | 2 | 3 | 4 | -1; day: string } {
  const dayIdx = date.getDay();
  const day = DAYS_RFC[dayIdx];
  const dayOfMonth = date.getDate();
  const week = Math.ceil(dayOfMonth / 7);
  // Check if it's the last occurrence
  const nextSameDay = new Date(date);
  nextSameDay.setDate(dayOfMonth + 7);
  const isLast = nextSameDay.getMonth() !== date.getMonth();
  const pos = isLast ? -1 : ([1, 2, 3, 4] as const)[week - 1] ?? 4;
  return { position: pos, day };
}

export function describeRule(rule: RecurrenceRule, refDate?: Date | null): string {
  const { frequency, interval, daysOfWeek, monthlyMode, monthlyDate, monthlyPosition, monthlyDay, end } = rule;

  let base = '';
  if (frequency === 'DAILY') {
    base = interval === 1 ? 'Daily' : `Every ${interval} days`;
  } else if (frequency === 'WEEKLY') {
    if (daysOfWeek && daysOfWeek.length > 0) {
      const dayNames = daysOfWeek.map((d) => DAYS_FULL[DAYS_RFC.indexOf(d)] ?? d).join(', ');
      base = interval === 1
        ? `Weekly on ${dayNames}`
        : `Every ${interval} weeks on ${dayNames}`;
    } else if (refDate) {
      const dayName = DAYS_FULL[refDate.getDay()];
      base = interval === 1 ? `Weekly on ${dayName}` : `Every ${interval} weeks on ${dayName}`;
    } else {
      base = interval === 1 ? 'Weekly' : `Every ${interval} weeks`;
    }
  } else if (frequency === 'MONTHLY') {
    if (monthlyMode === 'DATE' && monthlyDate) {
      base = interval === 1
        ? `Monthly on day ${monthlyDate}`
        : `Every ${interval} months on day ${monthlyDate}`;
    } else if (monthlyMode === 'WEEKDAY' && monthlyPosition != null && monthlyDay) {
      const ordIdx = monthlyPosition === -1 ? 4 : monthlyPosition - 1;
      const ordLabel = ORDINALS[ordIdx] ?? String(monthlyPosition);
      const dayName = DAYS_FULL[DAYS_RFC.indexOf(monthlyDay)] ?? monthlyDay;
      base = interval === 1
        ? `Monthly on the ${ordLabel} ${dayName}`
        : `Every ${interval} months on the ${ordLabel} ${dayName}`;
    } else if (refDate) {
      const dayOfMonth = refDate.getDate();
      base = interval === 1
        ? `Monthly on day ${dayOfMonth}`
        : `Every ${interval} months on day ${dayOfMonth}`;
    } else {
      base = interval === 1 ? 'Monthly' : `Every ${interval} months`;
    }
  } else if (frequency === 'YEARLY') {
    base = interval === 1 ? 'Annually' : `Every ${interval} years`;
  }

  if (end.type === 'ON' && end.date) {
    base += `, until ${end.date}`;
  } else if (end.type === 'AFTER' && end.count) {
    base += `, ${end.count} time${end.count === 1 ? '' : 's'}`;
  }

  return base;
}

// ─────────────────────────────────────────────────────────────────────────────
// Custom recurrence modal
// ─────────────────────────────────────────────────────────────────────────────

interface CustomModalProps {
  isOpen: boolean;
  onClose: () => void;
  initial: RecurrenceRule;
  onSave: (rule: RecurrenceRule) => void;
}

function CustomModal({ isOpen, onClose, initial, onSave }: CustomModalProps) {
  const [freq, setFreq] = useState<RecurrenceRule['frequency']>(initial.frequency);
  const [interval, setInterval] = useState(String(initial.interval ?? 1));
  const [daysOfWeek, setDaysOfWeek] = useState<string[]>(initial.daysOfWeek ?? []);
  const [monthlyMode, setMonthlyMode] = useState<'DATE' | 'WEEKDAY'>(initial.monthlyMode ?? 'DATE');
  const [monthlyDate, setMonthlyDate] = useState(String(initial.monthlyDate ?? 1));
  const [monthlyPosition, setMonthlyPosition] = useState<string>(String(initial.monthlyPosition ?? 1));
  const [monthlyDay, setMonthlyDay] = useState<string>(initial.monthlyDay ?? 'MO');
  const [endType, setEndType] = useState<'NEVER' | 'ON' | 'AFTER'>(initial.end.type);
  const [endDate, setEndDate] = useState(initial.end.date ?? '');
  const [endCount, setEndCount] = useState(String(initial.end.count ?? 5));

  const toggleDay = (d: string) => {
    setDaysOfWeek((prev) =>
      prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]
    );
  };

  const save = () => {
    const n = Math.max(1, parseInt(interval) || 1);
    const rule: RecurrenceRule = {
      frequency: freq,
      interval: n,
      end: endType === 'ON'
        ? { type: 'ON', date: endDate || undefined }
        : endType === 'AFTER'
        ? { type: 'AFTER', count: Math.max(1, parseInt(endCount) || 1) }
        : { type: 'NEVER' },
    };
    if (freq === 'WEEKLY' && daysOfWeek.length > 0) {
      rule.daysOfWeek = daysOfWeek;
    }
    if (freq === 'MONTHLY') {
      rule.monthlyMode = monthlyMode;
      if (monthlyMode === 'DATE') {
        rule.monthlyDate = Math.max(1, Math.min(31, parseInt(monthlyDate) || 1));
      } else {
        rule.monthlyPosition = (parseInt(monthlyPosition) as 1 | 2 | 3 | 4 | -1) || 1;
        rule.monthlyDay = monthlyDay;
      }
    }
    onSave(rule);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Custom recurrence">
      <div className="space-y-5">
        {/* Every N [freq] */}
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-700 shrink-0">Every</span>
          <input
            type="number"
            min={1}
            value={interval}
            onChange={(e) => setInterval(e.target.value)}
            className="input w-16 text-center"
          />
          <select value={freq} onChange={(e) => setFreq(e.target.value as RecurrenceRule['frequency'])} className="input flex-1">
            <option value="DAILY">day(s)</option>
            <option value="WEEKLY">week(s)</option>
            <option value="MONTHLY">month(s)</option>
            <option value="YEARLY">year(s)</option>
          </select>
        </div>

        {/* Weekly: day chips */}
        {freq === 'WEEKLY' && (
          <div>
            <p className="text-xs text-gray-500 mb-2">On</p>
            <div className="flex gap-1.5">
              {DAYS_SHORT.map((label, i) => {
                const rfc = DAYS_RFC[i];
                const on = daysOfWeek.includes(rfc);
                return (
                  <button
                    key={rfc}
                    type="button"
                    onClick={() => toggleDay(rfc)}
                    className={`w-9 h-9 rounded-full text-xs font-medium border transition-colors ${
                      on
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Monthly options */}
        {freq === 'MONTHLY' && (
          <div className="space-y-3">
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="monthly-mode"
                  checked={monthlyMode === 'DATE'}
                  onChange={() => setMonthlyMode('DATE')}
                  className="text-indigo-600"
                />
                <span className="text-sm text-gray-700">On day</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="monthly-mode"
                  checked={monthlyMode === 'WEEKDAY'}
                  onChange={() => setMonthlyMode('WEEKDAY')}
                  className="text-indigo-600"
                />
                <span className="text-sm text-gray-700">On the</span>
              </label>
            </div>
            {monthlyMode === 'DATE' ? (
              <input
                type="number"
                min={1}
                max={31}
                value={monthlyDate}
                onChange={(e) => setMonthlyDate(e.target.value)}
                className="input w-20"
              />
            ) : (
              <div className="flex gap-2">
                <select
                  value={monthlyPosition}
                  onChange={(e) => setMonthlyPosition(e.target.value)}
                  className="input flex-1"
                >
                  {ORDINAL_LABELS.map((label, i) => (
                    <option key={label} value={i === 4 ? -1 : i + 1}>{label}</option>
                  ))}
                </select>
                <select
                  value={monthlyDay}
                  onChange={(e) => setMonthlyDay(e.target.value)}
                  className="input flex-1"
                >
                  {DAYS_RFC.map((rfc, i) => (
                    <option key={rfc} value={rfc}>{DAYS_FULL[i]}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        {/* End condition */}
        <div>
          <p className="text-xs text-gray-500 mb-2">Ends</p>
          <div className="space-y-2.5">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="end-type"
                checked={endType === 'NEVER'}
                onChange={() => setEndType('NEVER')}
                className="text-indigo-600"
              />
              <span className="text-sm text-gray-700">Never</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="end-type"
                checked={endType === 'ON'}
                onChange={() => setEndType('ON')}
                className="text-indigo-600"
              />
              <span className="text-sm text-gray-700 shrink-0">On date</span>
              {endType === 'ON' && (
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="input ml-2"
                />
              )}
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="end-type"
                checked={endType === 'AFTER'}
                onChange={() => setEndType('AFTER')}
                className="text-indigo-600"
              />
              <span className="text-sm text-gray-700 shrink-0">After</span>
              {endType === 'AFTER' && (
                <div className="flex items-center gap-2 ml-2">
                  <input
                    type="number"
                    min={1}
                    value={endCount}
                    onChange={(e) => setEndCount(e.target.value)}
                    className="input w-16 text-center"
                  />
                  <span className="text-sm text-gray-600">occurrence{parseInt(endCount) !== 1 ? 's' : ''}</span>
                </div>
              )}
            </label>
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="flex-1 btn btn-secondary">
            Cancel
          </button>
          <button type="button" onClick={save} className="flex-1 btn btn-primary">
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main RecurrenceSelect component
// ─────────────────────────────────────────────────────────────────────────────

type BuiltInOption = 'none' | 'daily' | 'weekly' | 'biweekly' | 'monthly-date' | 'monthly-weekday' | 'annually' | 'weekdays' | 'custom';

export function RecurrenceSelect({ value, onChange, referenceDate }: RecurrenceSelectProps) {
  const [customOpen, setCustomOpen] = useState(false);
  const refDate = parseRefDate(referenceDate);

  const getBuiltIn = (): BuiltInOption => {
    if (!value) return 'none';
    const { frequency, interval, daysOfWeek, monthlyMode } = value;
    if (frequency === 'DAILY' && interval === 1 && !daysOfWeek?.length) return 'daily';
    if (frequency === 'WEEKLY' && interval === 2 && !daysOfWeek?.length) return 'biweekly';
    if (frequency === 'WEEKLY' && interval === 1 && !daysOfWeek?.length) return 'weekly';
    if (frequency === 'WEEKLY' && interval === 1 && daysOfWeek?.length === 5 &&
      ['MO','TU','WE','TH','FR'].every((d) => daysOfWeek.includes(d))) return 'weekdays';
    if (frequency === 'MONTHLY' && interval === 1 && monthlyMode === 'DATE') return 'monthly-date';
    if (frequency === 'MONTHLY' && interval === 1 && monthlyMode === 'WEEKDAY') return 'monthly-weekday';
    if (frequency === 'YEARLY' && interval === 1) return 'annually';
    return 'custom';
  };

  const current = getBuiltIn();
  const isCustom = current === 'custom';

  const applyBuiltIn = (opt: BuiltInOption) => {
    if (opt === 'none') { onChange(null); return; }
    const never = { type: 'NEVER' as const };
    if (opt === 'daily') { onChange({ frequency: 'DAILY', interval: 1, end: never }); return; }
    if (opt === 'weekly') {
      const rule: RecurrenceRule = { frequency: 'WEEKLY', interval: 1, end: never };
      if (refDate) rule.daysOfWeek = [DAYS_RFC[refDate.getDay()]];
      onChange(rule);
      return;
    }
    if (opt === 'biweekly') {
      const rule: RecurrenceRule = { frequency: 'WEEKLY', interval: 2, end: never };
      if (refDate) rule.daysOfWeek = [DAYS_RFC[refDate.getDay()]];
      onChange(rule);
      return;
    }
    if (opt === 'monthly-date') {
      const rule: RecurrenceRule = {
        frequency: 'MONTHLY', interval: 1, end: never,
        monthlyMode: 'DATE', monthlyDate: refDate ? refDate.getDate() : 1,
      };
      onChange(rule);
      return;
    }
    if (opt === 'monthly-weekday') {
      const ord = refDate ? ordinalForDate(refDate) : { position: 1 as 1, day: 'MO' };
      onChange({ frequency: 'MONTHLY', interval: 1, end: never, monthlyMode: 'WEEKDAY', monthlyPosition: ord.position, monthlyDay: ord.day });
      return;
    }
    if (opt === 'annually') { onChange({ frequency: 'YEARLY', interval: 1, end: never }); return; }
    if (opt === 'weekdays') { onChange({ frequency: 'WEEKLY', interval: 1, end: never, daysOfWeek: ['MO','TU','WE','TH','FR'] }); return; }
    if (opt === 'custom') {
      setCustomOpen(true);
    }
  };

  const weekdayName = refDate ? DAYS_FULL[refDate.getDay()] : 'day';
  const monthDayNum = refDate ? refDate.getDate() : null;
  const monthOrd = refDate ? ordinalForDate(refDate) : null;
  const monthOrdLabel = monthOrd
    ? `${ORDINALS[monthOrd.position === -1 ? 4 : monthOrd.position - 1]} ${DAYS_FULL[DAYS_RFC.indexOf(monthOrd.day)]}`
    : null;

  const selectValue = isCustom ? 'custom' : current;

  return (
    <>
      <div>
        <select
          value={selectValue}
          onChange={(e) => applyBuiltIn(e.target.value as BuiltInOption)}
          className="input"
        >
          <option value="none">Does not repeat</option>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly on {weekdayName}</option>
          <option value="biweekly">Every 2 weeks on {weekdayName}</option>
          {monthDayNum && <option value="monthly-date">Monthly on day {monthDayNum}</option>}
          {monthOrdLabel && <option value="monthly-weekday">Monthly on the {monthOrdLabel}</option>}
          <option value="annually">Annually</option>
          <option value="weekdays">Every weekday (Mon–Fri)</option>
          {isCustom && value && (
            <option value="custom">{describeRule(value, refDate)}</option>
          )}
          <option value="custom">Custom…</option>
        </select>

        {value && (
          <p className="mt-1 text-xs text-gray-500">{describeRule(value, refDate)}</p>
        )}
      </div>

      <CustomModal
        isOpen={customOpen}
        onClose={() => setCustomOpen(false)}
        initial={value ?? { frequency: 'WEEKLY', interval: 1, end: { type: 'NEVER' } }}
        onSave={(rule) => {
          onChange(rule);
          setCustomOpen(false);
        }}
      />
    </>
  );
}
